import Cookies from 'js-cookie'

// Types
export type TokenType = 'Access-Token' | 'Refresh-Token' | 'User-Chat-Token'
export type JWTToken = string
export type RefreshToken = string

interface TokenConfig {
  readonly ACCESS_TOKEN: string
  readonly COOKIE_OPTIONS: {
    expires: number
    path: string
  }
  readonly REFRESH_TOKEN: string
  readonly USER_CHAT_TOKEN: string
}

// Configuration
const TOKEN_CONFIG: TokenConfig = {
  ACCESS_TOKEN: 'chaine-access-token',
  COOKIE_OPTIONS: {
    expires: 60 * 60 * 24 * 90,
    path: '/' // 90 days
  },
  REFRESH_TOKEN: 'chaine-refresh-token',
  USER_CHAT_TOKEN: 'chaine-user-chat-token'
} as const

export const TOKEN_UPDATED_EVENT = 'chain:token-updated'

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined'

/**
 * Determines if we should use localStorage instead of cookies
 * - Uses localStorage if URL includes "embed" or if running in an iframe
 * - Otherwise uses cookies
 */
const shouldUseLocalStorage = (): boolean => {
  if (!isBrowser) return false
  const isInIframe = window.self !== window.top
  const urlIncludesEmbed = window.location.pathname.includes('embed') || window.location.href.includes('embed')
  return isInIframe || urlIncludesEmbed
}

const getLocalStorageItem = (key: string): string | null => {
  if (!isBrowser) return null

  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const setLocalStorageItem = (key: string, value: string): void => {
  if (!isBrowser) return

  try {
    localStorage.setItem(key, value)
  } catch {
    // Ignore storage write failures and rely on the primary cookie session.
  }
}

const removeLocalStorageItem = (key: string): void => {
  if (!isBrowser) return

  try {
    localStorage.removeItem(key)
  } catch {
    // Ignore storage removal failures.
  }
}

const getResolvedToken = (tokenType: TokenType): JWTToken | RefreshToken | null => {
  const tokenName = tokenUtils.getTokenName(tokenType)
  const cookieToken = Cookies.get(tokenName) || null
  const localStorageToken = getLocalStorageItem(tokenName)

  return cookieToken || localStorageToken
}

export const tokenUtils = {
  emitTokenUpdatedEvent: (tokenType: TokenType): void => {
    if (!isBrowser) return
    window.dispatchEvent(new CustomEvent(TOKEN_UPDATED_EVENT, { detail: { tokenType } }))
  },
  /**
   * Clears all cookies from local storage
   */
  clearLocalStore: (): void => {
    if (!isBrowser) return

    // Remove cookies with the same options they were set with
    Object.keys(Cookies.get()).forEach((cookieName) => {
      Cookies.remove(cookieName, { path: '/' })
    })

    try {
      Object.keys(localStorage).forEach((localStorageKey) => {
        localStorage.removeItem(localStorageKey)
      })
    } catch {
      // Ignore localStorage cleanup failures and rely on cookie cleanup.
    }

    tokenUtils.emitTokenUpdatedEvent('Access-Token')
    tokenUtils.emitTokenUpdatedEvent('Refresh-Token')
    tokenUtils.emitTokenUpdatedEvent('User-Chat-Token')
  },

  /**
   * Gets cookie data with type safety
   */
  getCookie: <T>(key: string): T | null => {
    if (!isBrowser) return null
    const data = Cookies.get(key)
    return data ? JSON.parse(data) : null
  },

  /**
   * Gets data from local storage
   */
  getDataFromLocalStore: (key: string): string | null => {
    return getLocalStorageItem(key)
  },

  /**
   * Gets a token by type
   * Uses cookies as the primary session source and falls back to localStorage
   * so embed and non-embed contexts can share auth state safely.
   */
  getToken: (tokenType: TokenType): JWTToken | RefreshToken | null => {
    if (!isBrowser) return null
    return getResolvedToken(tokenType)
  },

  /**
   * Gets the token name based on token type
   */
  getTokenName: (tokenType: TokenType): string => {
    const tokenMap = {
      'Access-Token': TOKEN_CONFIG.ACCESS_TOKEN,
      'Refresh-Token': TOKEN_CONFIG.REFRESH_TOKEN,
      'User-Chat-Token': TOKEN_CONFIG.USER_CHAT_TOKEN
    }
    return tokenMap[tokenType] || ''
  },

  /**
   * Gets user ID from access token
   */
  getUserID: (): string | null => {
    if (!isBrowser) return null
    try {
      const token = tokenUtils.getToken('Access-Token')
      if (!token) return null

      const parsedToken = tokenUtils.parseJwt(token)
      return parsedToken?.username || null
    } catch {
      return null
    }
  },

  /**
   * Checks if user is authenticated by verifying access token existence
   */
  isAuthenticated: (): boolean => {
    if (!isBrowser) return false
    return tokenUtils.getToken('Access-Token') !== null
  },

  /**
   * Parses a JWT token and returns the payload
   */
  parseJwt: (token: string): { username: string } | null => {
    if (!isBrowser) return null
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )

      return JSON.parse(jsonPayload)
    } catch {
      return null
    }
  },

  /**
   * Removes a token
   * Uses localStorage if URL includes "embed" or if in iframe, otherwise uses cookie storage
   */
  removeToken: (tokenType: TokenType): void => {
    if (!isBrowser) return
    const tokenName = tokenUtils.getTokenName(tokenType)

    removeLocalStorageItem(tokenName)
    Cookies.remove(tokenName, { path: '/' })

    tokenUtils.emitTokenUpdatedEvent(tokenType)
  },

  /**
   * Sets cookie data with type safety
   */
  setCookie: <T>(key: string, data: T): void => {
    if (!isBrowser) return
    Cookies.set(key, JSON.stringify(data), TOKEN_CONFIG.COOKIE_OPTIONS)
  },

  /**
   * Sets a token
   * Uses localStorage if URL includes "embed" or if in iframe, otherwise uses cookies
   */
  setToken: (tokenType: TokenType, token: JWTToken | RefreshToken): void => {
    if (!isBrowser) return
    const tokenName = tokenUtils.getTokenName(tokenType)

    if (shouldUseLocalStorage()) {
      setLocalStorageItem(tokenName, token)
    }

    Cookies.set(tokenName, token, TOKEN_CONFIG.COOKIE_OPTIONS)

    tokenUtils.emitTokenUpdatedEvent(tokenType)
  }
}
